import type Big from 'big.js';
import { GetPrice, GetSizes } from 'smg/customer-api/lineitems';
import * as models from 'smg/customer-api/models';
import { CustomizerEngine, ImageLayer, ShapeLayer, type ShapeConfig } from 'smg/customizer';
import { SetupBuilderKeyboardShortcuts } from 'smg/customizer/keyboard-shortcuts';
import { BuilderShareDesign } from 'smg/customizer/share-design';
import { ShowError } from 'smg/errors';
import * as uploads from 'smg/uploads';
import { DollarFormat } from 'smg/util';
import * as Swiper from 'swiper';

import { Relref } from '../../hugo';
import { PinDesignPersistence, PinDesignState } from '../../no-minimum-custom-pins/design-persistence';
import { CanvasSize, CornerRadiusPx, GeometryForType, PRINT_DPI, PrintBleedPx, SHAPE_TYPES, ShapeClipFunc, WORKING_DPI } from '../../no-minimum-custom-pins/geometry';
import { PinImageUploadUI } from '../../no-minimum-custom-pins/image-upload-ui';
import { PinShadingOverlay } from '../../no-minimum-custom-pins/shading-overlay';
import { PinStageView } from '../../no-minimum-custom-pins/stage-view';
import { PinTextDropUI } from '../../no-minimum-custom-pins/text-drop-ui';
import { BindTourHelpButtons, StartPinBuilderTour } from '../../no-minimum-custom-pins/tour';
import { SetupBuilderAddToCart } from '../../product-customizer/add-to-cart';
import { BindResetDesignButtons, BindShareDesignButtons } from '../../product-customizer/share-design-ui';
import { ConfirmSpellCheck } from '../../spell-check-modal';

// Cart item being edited via ?cartItem= — replaced (added then deleted) on add-to-cart
let editingCartItemID = '';

// Photoreal polished-silver blank per shape (marketing renders, trimmed to ink edge) —
// stretched onto the canvas as the background layer, coins' plating-photo pattern
const BLANK_IMAGES: Record<string, string> = {
    'Circle': 'blank-circle.png',
    'Square': 'blank-square.png',
    'Diamond': 'blank-diamond.png',
    'Rectangle': 'blank-rectangle.png',
};

// Shape + size selectors render the chosen blank through the shared CustomizerEngine,
// driven by the geometry map + GetSizes(). The engine is recreated per shape/size (its
// internal resolution is the pin's own); PinStageView draws the pin at its actual size
// and owns its zoom, so a small pin looks as small as it really is.
void main();

async function main() {
    const $selector = document.getElementById('pin-shape-selector');
    const $sizeSelector = document.getElementById('pin-size-selector');
    // Shared builder id (see product-customizer.scss) so the coin page's responsive
    // preview sizing — including the 100vh clamp on short viewports — applies here.
    const $preview = document.getElementById('coin-preview-container');
    if (!$selector || !$sizeSelector || !$preview) {
        return;
    }

    // Canvas starts hidden (see no-minimum-custom-pins.scss) — the markup's spinner
    // covers the stage until the initial selection/restore has fully landed
    const $loadingOverlay = document.getElementById('stage-view-loading');

    const sizes = await GetSizes('lapel pin', 'Made in USA - Rush Pin');

    // System rows per offered shape, small to large — the DB is the source of truth for
    // which sizes exist; the SHAPE_TYPES allowlist keeps novelty blanks out.
    const sizesForType = (type: string): models.LineItemTypeSize[] =>
        sizes.filter(s => s.type === type).
            sort((a, b) => a.width.cmp(b.width) || a.height.cmp(b.height));

    const shapes = SHAPE_TYPES.filter(type => sizesForType(type).length > 0);

    if (shapes.length === 0) {
        ShowError(new Error('No pin shapes available'));
        return;
    }

    let engine: CustomizerEngine | undefined;
    let currentSize: models.LineItemTypeSize | undefined;
    let bgColor = '';
    let bgLayer: ShapeLayer | undefined;

    const imageUploadUI = new PinImageUploadUI();

    // Text drop (free-position text blocks) — blocks show as rows in the shared layer list
    const textDropUI = new PinTextDropUI();
    imageUploadUI.setTextDropUI(textDropUI);

    // Shading overlay: ink-edge indent seams + silhouette rim (presentation only)
    const shadingOverlay = new PinShadingOverlay();

    // The pin shows at actual size, and zooms no further than filling the stage
    const stageView = new PinStageView();
    // An empty pin leaves room under it for its own Upload · Add Text row
    imageUploadUI.onEmptyChange = empty => stageView.setEmpty(empty);

    // Selections are exclusive across the two owners
    textDropUI.onSelect = () => imageUploadUI.deselectAll();
    imageUploadUI.onLayerSelected = () => textDropUI.deselectAll();

    document.querySelectorAll<HTMLButtonElement>('.add-text-drop-btn').forEach($btn => {
        $btn.addEventListener('click', e => {
            // The upload-zone instance sits inside the zone's own click-to-upload target
            e.stopPropagation();
            void textDropUI.addBlock();
        });
    });
    // (Card re-equalization on text changes rides the pin-uploads-changed dispatch from
    // the layer-list re-render — no separate listener needed.)

    // The background-color layer's shape — matches ShapeClipFunc's printable area (the
    // full blank, corners rounded like the physical pins'): these pins print edge-to-edge,
    // so a background color covers the metal completely.
    const shapeConfig = (size: models.LineItemTypeSize, fill: string): ShapeConfig => {
        const { width, height } = CanvasSize(size);
        const geometry = GeometryForType(size.type);
        const corner = CornerRadiusPx();
        if (geometry.maskKind === 'circle') {
            return { type: 'circle', radius: width / 2, x: width / 2, y: height / 2, fill };
        }
        if (geometry.rotationDeg) {
            // Diamond = a rounded rect rotated 45° (Konva's RegularPolygon can't round its
            // vertices), side = the distance between adjacent canvas edge midpoints,
            // rotating around its own center via the offset.
            const side = Math.round(width / Math.SQRT2);
            return {
                type: 'rect',
                width: side,
                height: side,
                cornerRadius: corner,
                x: width / 2,
                y: height / 2,
                offsetX: side / 2,
                offsetY: side / 2,
                rotation: 45,
                fill,
            };
        }
        return { type: 'rect', width, height, cornerRadius: corner, x: 0, y: 0, fill };
    };

    // Solid color above the blank, below future art — mirrors the coin page's
    // CoinBackgroundColor layer (zIndex 1 over the plating base). Empty color = pin
    // shows bare polished silver.
    const applyBackground = async (): Promise<void> => {
        if (!engine || !currentSize) {
            return;
        }
        if (!bgColor) {
            if (bgLayer) {
                engine.removeLayer('pin-bg-color');
                bgLayer = undefined;
            }
            return;
        }
        if (!bgLayer) {
            bgLayer = new ShapeLayer('pin-bg-color', { userEditable: false, resizable: false, zIndex: 1 });
            await engine.addLayer('pin-bg-color', bgLayer);
        }
        bgLayer.setShape(shapeConfig(currentSize, bgColor));
    };

    // Left and right cards always match height. Flexbox stretch handles it when the
    // right card's content fits the row, but Firefox won't propagate the design tab's
    // intrinsic height into the row (min-height: max-content resolves as auto here), so
    // when the content outgrows the stretched height we pin an explicit min-height and
    // let stretch pull the other column up to match.
    const $customizationCard = document.getElementById('customization-card');
    const $previewCard = document.querySelector<HTMLElement>('.coin-preview-card');
    const equalizeCards = (): void => {
        if (!$customizationCard || !$previewCard) {
            return;
        }
        $customizationCard.style.minHeight = '';
        $previewCard.style.minHeight = '';
        if (!window.matchMedia('(min-width: 992px)').matches) {
            return;
        }
        const spill = $customizationCard.scrollHeight - $customizationCard.clientHeight;
        if (spill > 1) {
            // Pin both sides — Firefox doesn't reliably stretch the sibling column when
            // only the card's min-height grows the row. scrollHeight is content-box, so
            // add the border delta back for an exact outer height.
            const borders = $customizationCard.offsetHeight - $customizationCard.clientHeight;
            const height = $customizationCard.scrollHeight + borders;
            $customizationCard.style.minHeight = `${height}px`;
            $previewCard.style.minHeight = `${height}px`;
        }
    };
    document.addEventListener('pin-uploads-changed', equalizeCards);

    // Coins' setupResponsive: below lg the preview section is sticky, so cap the square
    // stage to keep the whole sticky block within 50vh (measure the non-canvas chrome,
    // cap what's left). Every shape draws inside the same square stage.
    const $previewSection = document.getElementById('coin-preview-section');
    const applyResponsiveCap = (): void => {
        if (!$previewSection || !currentSize) {
            return;
        }
        const isSticky = getComputedStyle($previewSection).position === 'sticky';
        if (!isSticky) {
            $preview.style.maxHeight = '';
            $preview.style.maxWidth = '';
        } else {
            const prevMaxHeight = $preview.style.maxHeight;
            $preview.style.maxHeight = '0';
            const nonCanvasHeight = $previewSection.offsetHeight;
            $preview.style.maxHeight = prevMaxHeight;

            const available = Math.floor(window.innerHeight * 0.5) - nonCanvasHeight;
            if (available > 0) {
                $preview.style.maxHeight = `${available}px`;
                $preview.style.maxWidth = `${available}px`;
            } else {
                $preview.style.maxHeight = '';
                $preview.style.maxWidth = '';
            }
        }
        stageView.refresh();
    };

    // Live unit price (qty-break aware) + the "Buy more, save more" tier list, both
    // per the selected size — the server independently reprices at cart time
    const $unitPrice = document.getElementById('pin-unit-price');
    const $unitPriceMobile = document.getElementById('pin-unit-price-mobile');
    const $qtyInput = document.getElementById('pin-quantity') as HTMLInputElement | null;
    const $qtyInputMobile = document.getElementById('pin-quantity-mobile') as HTMLInputElement | null;

    const updatePrice = async (): Promise<void> => {
        if (!currentSize) {
            return;
        }
        const qty = Math.max(1, parseInt($qtyInput?.value ?? '1', 10) || 1);
        const unitPrice = await GetPrice('lapel pin', 'Made in USA - Rush Pin', currentSize.name, qty);
        const formatted = DollarFormat(unitPrice);
        if ($unitPrice) {
            $unitPrice.textContent = formatted;
        }
        if ($unitPriceMobile) {
            $unitPriceMobile.textContent = formatted;
        }
    };

    const updatePricingTiers = (): void => {
        if (!currentSize) {
            return;
        }
        // Sub-100 breaks are the same for every shape/size, so those bullets are exact.
        // At 100+ each size has its own volume pricing — one honest teaser bullet with
        // the true floor across everything we offer (the live price above is per-size).
        const subBreaks = (currentSize.networkLineItemTypeSize?.prices ?? []).filter(p => p.quantity < 100);
        const tiers: string[] = [];
        for (let i = 0; i < subBreaks.length; i++) {
            const price = subBreaks[i];
            const nextQty = i < subBreaks.length - 1 ? subBreaks[i + 1].quantity : 100;
            tiers.push(`<li class="pb-1">${price.quantity}-${nextQty - 1} pins for <span class="text-secondary fw-bold">${DollarFormat(price.price)} each</span></li>`);
        }

        let bulkFloor: Big | undefined;
        for (const type of shapes) {
            for (const size of sizesForType(type)) {
                for (const price of size.networkLineItemTypeSize?.prices ?? []) {
                    if (price.quantity >= 100 && (!bulkFloor || price.price.lt(bulkFloor))) {
                        bulkFloor = price.price;
                    }
                }
            }
        }
        if (bulkFloor) {
            tiers.push(`<li class="pb-1">100+ pins for <span class="text-secondary fw-bold">as low as ${DollarFormat(bulkFloor)} each</span> <span class="text-muted">(varies by size)</span></li>`);
        }

        const $mobile = document.getElementById('pricing-tiers-mobile');
        if ($mobile) {
            $mobile.innerHTML = tiers.join('');
        }
        const $desktop = document.getElementById('pricing-tiers-desktop');
        if ($desktop) {
            $desktop.innerHTML = tiers.join('');
        }
    };

    // The two quantity inputs (desktop CTA / mobile preview column) mirror each other;
    // #pin-quantity stays the canonical one the cart flow reads
    $qtyInput?.addEventListener('input', () => {
        if ($qtyInputMobile) {
            $qtyInputMobile.value = $qtyInput.value;
        }
        void updatePrice();
    });
    $qtyInputMobile?.addEventListener('input', () => {
        if ($qtyInput) {
            $qtyInput.value = $qtyInputMobile.value;
        }
        void updatePrice();
    });

    // Shape and size switches can overlap: a blank photo still loading on a slow
    // connection when the customer picks another shape. Each build has a generation, and
    // one a newer build has replaced stops at its next await instead of finishing onto
    // the newer canvas (the old shape's blank on the new stage, uploads cropped to it).
    let renderGeneration = 0;

    const renderShape = async (size: models.LineItemTypeSize): Promise<void> => {
        const generation = ++renderGeneration;
        const replaced = (): boolean => generation !== renderGeneration;
        const { width, height } = CanvasSize(size);
        currentSize = size;

        // Uploads and text blocks survive the engine teardown: snapshot, re-add after rebuild
        imageUploadUI.captureTransforms();
        textDropUI.captureTransforms();
        engine?.destroy();
        bgLayer = undefined;
        const built = new CustomizerEngine({
            container: 'pin-canvas',
            width,
            height,
            exportScale: PRINT_DPI / WORKING_DPI,
            printCrop: { x: 0, y: 0, width, height },
            printDpi: PRINT_DPI,
            printBleedPx: PrintBleedPx(),
            printBleedClip: ShapeClipFunc(size),
        });
        engine = built;

        stageView.attach(built, size);

        // Re-hook the autosave + share-cache invalidation onto the fresh engine
        persistence.hookEngine(built);
        built.on('significantChange', shareDesign.clearCache);

        const blank = new ImageLayer('pin-blank', { userEditable: false, isBackground: true });
        await blank.setImage(`${Relref('/')}img/no-minimum-custom-pins/${BLANK_IMAGES[size.type] ?? BLANK_IMAGES.Circle}`);
        if (replaced()) {
            return;
        }
        await built.addLayer('pin-blank', blank);
        if (replaced()) {
            return;
        }
        // Stretch the photo to the canvas exactly — each blank is shot at ~its shape's
        // aspect, so the residual distortion is a couple percent at most. Background
        // images anchor at the top-left (no center offset, unlike user images), so the
        // fill needs position 0,0.
        const blankNatural = blank.getNaturalSize();
        if (blankNatural.width > 0 && blankNatural.height > 0) {
            blank.setScale(width / blankNatural.width, height / blankNatural.height);
        }
        blank.setPosition(0, 0);

        await applyBackground();
        if (replaced()) {
            return;
        }
        await imageUploadUI.attachTo(built, size);
        if (replaced()) {
            return;
        }
        await textDropUI.attachTo(built, size);
        if (replaced()) {
            return;
        }
        await shadingOverlay.attachTo(built, size);
        if (replaced()) {
            return;
        }
        applyResponsiveCap();
        void updatePrice();
        updatePricingTiers();

        // After layout settles: equalizeCards can change the preview card's height (and
        // with it the canvas container), so the stage re-measures AFTER it or it keeps
        // stale dimensions and clips the bottom of the pin.
        requestAnimationFrame(() => {
            applyResponsiveCap();
            equalizeCards();
            stageView.refresh();
        });
    };

    // The DB names are "0.75\" Circle" / "1\" × 0.75\" Rectangle" — the shape row already
    // says the shape, so size buttons show just the dimensions.
    const sizeLabel = (size: models.LineItemTypeSize): string =>
        size.name.replace(` ${size.type}`, '') || `${size.width.toString()}"`;

    const renderSizeButtons = (type: string, selected: models.LineItemTypeSize): void => {
        $sizeSelector.innerHTML = '';
        for (const size of sizesForType(type)) {
            const $btn = document.createElement('button');
            $btn.type = 'button';
            $btn.className = 'btn btn-outline-primary';
            $btn.textContent = sizeLabel(size);
            if (size.id === selected.id) {
                $btn.classList.add('active');
            }
            $btn.addEventListener('click', () => {
                $sizeSelector.querySelectorAll('button').forEach($b => $b.classList.remove('active'));
                $btn.classList.add('active');
                void renderShape(size);
            });
            $sizeSelector.appendChild($btn);
        }
    };

    // Default size per shape: 1" where offered, else the smallest
    const defaultSizeForType = (type: string): models.LineItemTypeSize => {
        const typeSizes = sizesForType(type);
        return typeSizes.find(s => s.width.eq(1)) ?? typeSizes[0];
    };

    const selectShape = (type: string): void => {
        const size = defaultSizeForType(type);
        renderSizeButtons(type, size);
        void renderShape(size);
    };

    for (const type of shapes) {
        const $btn = document.createElement('button');
        $btn.type = 'button';
        $btn.className = 'btn btn-outline-primary';
        $btn.textContent = type;
        $btn.addEventListener('click', () => {
            $selector.querySelectorAll('button').forEach($b => $b.classList.remove('active'));
            $btn.classList.add('active');
            selectShape(type);
        });
        $selector.appendChild($btn);
    }

    // Select a shape + exact size programmatically (startup default and design restore)
    const applySelectionUI = async (type: string, size: models.LineItemTypeSize): Promise<void> => {
        $selector.querySelectorAll('button').forEach($b =>
            $b.classList.toggle('active', $b.textContent === type));
        renderSizeButtons(type, size);
        await renderShape(size);
    };

    window.addEventListener('resize', () => {
        applyResponsiveCap();
        equalizeCards();
        // equalizeCards can change the canvas container's height — re-fit last
        stageView.refresh();
    });

    // (The stage view watches the canvas box itself: on desktop CSS sizes it from the
    // viewport-capped column, so it also moves without a window resize.)

    // Background color picker
    const $bgInput = document.getElementById('pin-bg-input') as HTMLInputElement | null;
    const $bgGraphic = document.getElementById('pin-bg-graphic');
    const $bgRemove = document.getElementById('pin-bg-remove');
    if ($bgInput && $bgGraphic && $bgRemove) {
        $bgInput.addEventListener('input', () => {
            bgColor = $bgInput.value;
            $bgGraphic.style.background = bgColor;
            $bgRemove.classList.remove('d-none');
            void applyBackground();
            // The bg layer's setShape doesn't reliably reach the autosave hook
            void persistence.save();
            shareDesign.clearCache();
        });
        $bgRemove.addEventListener('click', () => {
            bgColor = '';
            $bgGraphic.style.background = '';
            $bgRemove.classList.add('d-none');
            void applyBackground();
            void persistence.save();
            shareDesign.clearCache();
        });
    }

    const persistence = new PinDesignPersistence(imageUploadUI, textDropUI, {
        currentSelection: () => currentSize ? {
            shapeType: currentSize.type,
            sizeID: currentSize.id ?? '',
            canvas: CanvasSize(currentSize),
            bgColor,
        } : null,
        applySelection: async state => {
            bgColor = state.bgColor;
            if ($bgInput && $bgGraphic && $bgRemove) {
                $bgGraphic.style.background = bgColor;
                if (bgColor) {
                    $bgInput.value = bgColor;
                }
                $bgRemove.classList.toggle('d-none', !bgColor);
            }
            // Saved size may no longer exist (DB changes) — fall back down the ladder;
            // the layer restores rescale against the state's saved canvas either way
            const type = shapes.includes(state.shapeType) ? state.shapeType : shapes[0];
            const size = sizesForType(type).find(s => s.id === state.sizeID) ?? defaultSizeForType(type);
            await applySelectionUI(type, size);
        },
    });

    const shareDesign = new BuilderShareDesign<PinDesignState>({
        storageKey: 'pin-share-cache',
        persistence,
        createRequest: state => new models.BuilderDesignCreateRequest({
            uploadIDs: state.imageStates.map(s => s.uploadID).filter(id => id > 0),
            uploadLineItem: {
                lineItemID: models.LineItemID.LINE_ITEMS_LAPEL_PIN,
                networkSkuid: models.NetworkSKUID.NETWORK_SK_US_NO_MINIMUM_CUSTOM_PINS,
            },
            designData: state,
        }),
    });

    // ?cartItem, ?order+?orderLineItem, and ?design win over localStorage.
    // Precedence: cartItem > orderLineItem > design (coins parity).
    const { restored, editingCartItemID: editingID } = await shareDesign.restoreDesign();
    editingCartItemID = editingID;
    if (!restored) {
        await applySelectionUI(shapes[0], defaultSizeForType(shapes[0]));
    }

    // Reveal after restore — double rAF + timeout so restored uploads/text have
    // actually painted before the canvas shows (coins' reveal sequence)
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            setTimeout(() => {
                document.getElementById('pin-canvas')?.style.setProperty('opacity', '1');
                if ($loadingOverlay) {
                    $loadingOverlay.style.opacity = '0';
                    setTimeout(() => $loadingOverlay.remove(), 200);
                }
            }, 0);
        });
    });

    BindShareDesignButtons(shareDesign);

    SetupBuilderKeyboardShortcuts({
        owners: [
            {
                selectedId: () => imageUploadUI.getSelectedLayerId(),
                selectedNode: () => imageUploadUI.getSelectedNode(),
                deleteSelected: id => imageUploadUI.deleteImageWithUndo(id),
            },
            {
                selectedId: () => textDropUI.getSelectedId(),
                selectedNode: () => textDropUI.getSelectedNode(),
                deleteSelected: id => textDropUI.deleteBlock(id),
            },
        ],
        engine: () => engine,
        // Delete/nudge bypass the pointer lock — freeze them while a process runs
        enabled: () => !imageUploadUI.isProcessing(),
        // Arrows move 0.01" (Shift 0.1") on the pin, the same distance at any zoom
        nudgeDesignPx: [0.01 * WORKING_DPI, 0.1 * WORKING_DPI],
    });

    setupAddToCart({
        imageUploadUI,
        textDropUI,
        shadingOverlay,
        persistence,
        shareDesign,
        current: () => ({ engine, size: currentSize }),
    });

    BindTourHelpButtons('.coin-builder-tour-btn');
    // Once the main-thread init burst has settled. The tour no-ops if the user has
    // already seen every step.
    if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => StartPinBuilderTour(), { timeout: 1500 });
    } else {
        setTimeout(() => StartPinBuilderTour(), 600);
    }

    // The LG gallery card gets coins' swiper config (no autoplay, drag to browse) —
    // overriding the global swiper-init autoplay instance
    const $lgGallery = document.querySelector<HTMLElement>('.lg-gallery-swiper');
    if ($lgGallery) {
        new Swiper.Swiper($lgGallery, {
            slidesPerView: 'auto',
            spaceBetween: 16,
            grabCursor: true,
        });
    }

    BindResetDesignButtons(shareDesign);
}

function setupAddToCart(args: {
    imageUploadUI: PinImageUploadUI;
    textDropUI: PinTextDropUI;
    shadingOverlay: PinShadingOverlay;
    persistence: PinDesignPersistence;
    shareDesign: BuilderShareDesign<PinDesignState>;
    /** The engine is recreated per shape/size switch — always read the live pair */
    current: () => { engine: CustomizerEngine | undefined; size: models.LineItemTypeSize | undefined };
}): void {
    const { imageUploadUI, textDropUI, shadingOverlay, persistence, shareDesign } = args;
    // Desktop CTA button + the fixed mobile bar's button run the same flow;
    // both lock while either is working so a double-tap can't double-submit
    const $addToCarts = ['pin-add-to-cart', 'pin-add-to-cart-mobile'].
        map(id => document.getElementById(id) as HTMLButtonElement | null).
        filter(($btn): $btn is HTMLButtonElement => $btn !== null);
    const $quantity = document.getElementById('pin-quantity') as HTMLInputElement | null;

    if ($addToCarts.length === 0 || !$quantity) {
        return;
    }

    SetupBuilderAddToCart({
        buttons: $addToCarts,
        $quantity,
        $loadingOverlay: document.getElementById('cart-loading-overlay'),
        validateContent: () => {
            if (!imageUploadUI.hasUploads() && !textDropUI.hasVisibleContent()) {
                return 'Please add an image or text to your pin to add to cart';
            }
            return null;
        },
        // Text blocks checked exact; uploaded artwork OCR'd server-side. settledUploadIDs
        // gates on in-flight uploads actually reaching S3.
        spellCheckItems: async () => [{
            text: textDropUI.visibleBlockTexts().join('\n'),
            uploadIDs: await imageUploadUI.settledUploadIDs(),
        }],
        confirmSpellCheck: ConfirmSpellCheck,
        buildCartItem: async quantity => {
            const { engine, size } = args.current();
            if (!engine || !size) {
                throw new Error('builder is not ready yet');
            }

            // Preview keeps the blank + shading (what the customer sees); print files
            // are ink only — blank auto-hidden (isBackground), shading suppressed
            const preview = engine.exportAsDataURL('image/png', undefined, 2048);
            const [color, white] = await shadingOverlay.withHidden(() => Promise.all([
                engine.exportColorLayerForPrint(),
                engine.exportWhiteLayerForPrint(),
            ]));

            const artUploadIDs = await imageUploadUI.settledUploadIDs();

            const previewUploads = await uploads.NewFromURL([
                { name: 'pin.png', mime: 'image/png', url: preview },
            ]);
            // "." prefix hides print files from the customer
            const printUploads = await uploads.NewFromURL([
                { name: '.pin.png', mime: 'image/png', url: color },
                { name: '.pin-white.png', mime: 'image/png', url: white },
            ]);

            const options: { name: string; value: string }[] = [
                { name: 'Shape', value: size.type },
                { name: 'Size', value: size.name.replace(` ${size.type}`, '') || `${size.width.toString()}"` },
                // Customer-facing label for the Plating child sent below (the cart
                // renders `options`, not child line items)
                { name: 'Finish', value: 'Polished Silver' },
            ];
            textDropUI.visibleBlockTexts().forEach((text, i) => {
                options.push({ name: `Text ${i + 1}`, value: text });
            });

            // Persist the design first so the cart item pins a specific
            // (BuilderDesignID, Version); reuses the share cache so Share + Add to
            // Cart never create duplicate BuilderDesign rows for the same state
            const design = await shareDesign.ensureDesign();

            return {
                uploadID: previewUploads[0].uploadID,
                quantity,
                options,
                cartItemUploadIDs: [
                    ...artUploadIDs,
                    ...printUploads.map(u => u.uploadID),
                ],
                uploadLineItem: {
                    name: 'No Minimum Custom Pins',
                    lineItem: 'lapel pin',
                    type: 'Made in USA - Rush Pin',
                    size: size.name,
                    networkSKUID: models.NetworkSKUID.NETWORK_SK_US_NO_MINIMUM_CUSTOM_PINS,
                    childLineItems: [
                        { child: 'Plating', option: 'Silver' },
                    ],
                },
                builderDesignID: design.id,
                builderDesignVersion: design.version,
            };
        },
        editingCartItemID: () => editingCartItemID,
        onReplaced: () => {
            editingCartItemID = '';
        },
        afterAdded: () => {
            persistence.clear();
            window.location.href = Relref('/cart');
        },
    });
}
